/**
 * Daily cost trend — three daily series over a configurable window:
 *
 *   1. avg cost per audit for skip-cache audits (`skip_cache_all` /
 *      `force_refresh` — same bucketing as the cache-delta bars)
 *   2. avg cost per audit for audits allowed to use caches
 *   3. total audit spend that day (sum of run cost across both buckets)
 *
 * Rendered as two stacked panels sharing one x-axis rather than one chart with
 * two y-axes: per-audit cost lives in cents and daily spend in dollars, and a
 * dual-axis chart lets the reader infer a crossing that the data never claims.
 * Stacking the panels keeps the days aligned so the eye still reads them
 * together — a spend spike lines up with the average that produced it.
 *
 * Series colors are stepped per theme (the light steps match the emerald /
 * amber buckets on the cache-delta chart above, so the same audits keep the
 * same identity down the page). Both step sets are validated for CVD
 * separation and against their own surface; the light pair sits below 3:1
 * contrast, which is why the last point of each line carries a direct value
 * label.
 */
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle } from "lucide-react";
import { useTheme } from "@/contexts/useTheme";
import { ViewQueryButton } from "./ViewQueryButton";
import {
  formatDayLong,
  formatDayShort,
  formatInt,
  formatUsd,
  formatUsd2,
} from "./format";
import type { ApiEnvelope, DailyTrendData, DailyTrendRow } from "./types";

/** Window presets, in days. Must stay inside the route's 7–90 clamp. */
const WINDOWS = [7, 14, 30, 90] as const;

/**
 * Validated via the dataviz palette checker (`validate_palette.js`):
 * light — CVD ΔE 8.9, normal-vision ΔE 23.8, contrast WARN (labels supplied);
 * dark (surface #18181b) — CVD ΔE 8.4, normal-vision ΔE 19.8, contrast ≥3:1.
 */
const COLORS = {
  light: { skipCache: "#f59e0b", cached: "#10b981", total: "#3b82f6" },
  dark: { skipCache: "#c98500", cached: "#199e70", total: "#3987e5" },
} as const;

/**
 * Reserved status color for over-budget days — deliberately not one of the
 * series hues, so a flagged bar can never be misread as a fourth series. Fixed
 * across themes (clears 3:1 on both surfaces, and ΔE 26+ from the bar blue in
 * every CVD simulation). Never carries the meaning alone: the flagged day also
 * gets an icon-marked tick, a tooltip count, and a row in the breach table.
 */
const BREACH_COLOR = "#d03b3b";

/** Compact axis label, e.g. 12.5 → "$12.50", 0.0421 → "$0.042". */
function compactUsd(v: number): string {
  if (!Number.isFinite(v)) return "$0";
  if (v === 0) return "$0";
  return v < 1 ? `$${v.toFixed(3)}` : `$${v.toFixed(2)}`;
}

interface TooltipEntry {
  dataKey?: string | number;
  value?: number | null;
  color?: string;
}

/**
 * Shared tooltip for both panels. Lists every series at the hovered day (the
 * pointer never has to land on a line) with the value leading and the label
 * secondary, plus the audit counts that back the averages — a mean over two
 * audits reads very differently from one over four hundred.
 */
function TrendTooltip({
  active,
  payload,
  rows,
  fields,
  suppressed,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  rows: DailyTrendRow[];
  fields: Array<{ key: keyof DailyTrendRow; label: string; color: string }>;
  /**
   * `syncId` moves the crosshair into both panels at once, which is what we
   * want — but it also activates both tooltips, and two identical readouts is
   * noise. The panel the pointer is not in suppresses its own.
   */
  suppressed?: boolean;
}) {
  if (suppressed || !active || !payload?.length) return null;
  const day = (payload[0] as TooltipEntry & { payload?: DailyTrendRow })?.payload?.day;
  const row = rows.find((r) => r.day === day);
  if (!row) return null;

  return (
    <div className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 shadow-lg">
      <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
        {formatDayLong(row.day)}
      </div>
      <ul className="mt-1 space-y-0.5">
        {fields.map((f) => {
          const value = row[f.key] as number | null;
          return (
            <li key={String(f.key)} className="flex items-center gap-2 text-xs">
              <span
                className="inline-block h-0.5 w-3 rounded-full"
                style={{ backgroundColor: f.color }}
              />
              <span className="font-semibold text-zinc-900 dark:text-zinc-100 tabular-nums">
                {value == null ? "—" : formatUsd(value)}
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">{f.label}</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-1.5 border-t border-zinc-200 dark:border-zinc-700 pt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
        {formatInt(row.audit_count)} audits — {formatInt(row.skip_cache_count)}{" "}
        skip-cache, {formatInt(row.cached_count)} cached
      </div>
      {row.breach_count > 0 && (
        <div
          className="mt-1 flex items-center gap-1 text-[11px] font-medium"
          style={{ color: BREACH_COLOR }}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden />
          {formatInt(row.breach_count)} over-budget{" "}
          {row.breach_count === 1 ? "audit" : "audits"}
        </div>
      )}
    </div>
  );
}

/**
 * Direct value label on the last point that has data. Selective by design —
 * a number on every point would bury the shape the chart exists to show.
 */
function lastPointLabel(color: string, lastIndex: number) {
  // Props are widened to what recharts' LabelList `content` actually hands
  // over (`value` is `string | number | undefined` there, not our narrowed
  // `number | null`), then narrowed back here.
  return function LastPointLabel(props: {
    x?: number | string;
    y?: number | string;
    index?: number;
    value?: string | number;
  }) {
    const { x, y, index, value } = props;
    const numeric = Number(value);
    if (index !== lastIndex || value == null || !Number.isFinite(numeric)) return null;
    return (
      <text
        x={Number(x) + 6}
        y={Number(y)}
        dy={4}
        fill={color}
        fontSize={11}
        fontWeight={600}
        textAnchor="start"
      >
        {formatUsd(numeric)}
      </text>
    );
  };
}

/** Index of the last row whose series value is non-null (-1 when all empty). */
function lastPopulatedIndex(rows: DailyTrendRow[], key: keyof DailyTrendRow): number {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i][key] != null) return i;
  }
  return -1;
}

/**
 * Breach marker band — one cell per day, aligned under the spend bars.
 *
 * Deliberately NOT a recolor of the bars themselves. On a real 30-day window
 * roughly two-thirds of days carry at least one over-budget audit, so painting
 * those bars red made red the default and blue the exception — the flag stopped
 * being a flag, and the bar's color stopped meaning "spend". A separate
 * annotation row keeps the bars encoding one thing and reads as a marker layer
 * however many days trip.
 *
 * Laid out as plain flex cells inset by exactly the chart's y-axis width and
 * right margin, so cell N sits under band N — recharts centres a category band
 * the same way.
 */
function BreachMarkerBand({
  rows,
  insetLeft,
  insetRight,
}: {
  rows: DailyTrendRow[];
  insetLeft: number;
  insetRight: number;
}) {
  return (
    <div
      className="flex h-4 items-start"
      style={{ marginLeft: insetLeft, marginRight: insetRight }}
      aria-hidden
    >
      {rows.map((r) => (
        <div key={r.day} className="flex-1 flex justify-center">
          {r.breach_count > 0 && (
            <span
              title={`${r.day}: ${r.breach_count} over-budget ${
                r.breach_count === 1 ? "audit" : "audits"
              }`}
              style={{
                width: 0,
                height: 0,
                borderLeft: "4px solid transparent",
                borderRight: "4px solid transparent",
                borderBottom: `6px solid ${BREACH_COLOR}`,
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

interface DailyTrendChartProps {
  envelope: ApiEnvelope<DailyTrendData> | undefined;
  loading: boolean;
  days: number;
  onDaysChange: (days: number) => void;
}

export function DailyTrendChart({
  envelope,
  loading,
  days,
  onDaysChange,
}: DailyTrendChartProps) {
  const { resolvedTheme } = useTheme();
  const c = COLORS[resolvedTheme === "dark" ? "dark" : "light"];
  const axisTick = { fontSize: 11, fill: "#9ca3af" };
  const gridStroke = resolvedTheme === "dark" ? "#3f3f46" : "#e4e4e7";
  // The card surface — an active dot rings itself against it so an overlapping
  // pair of points stays two marks rather than one blob.
  const surface = resolvedTheme === "dark" ? "#18181b" : "#ffffff";

  const data = envelope?.data;
  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const hasData = rows.some((r) => r.audit_count > 0);

  const avgFields = [
    { key: "skip_cache_avg_cost" as const, label: "skip-cache", color: c.skipCache },
    { key: "cached_avg_cost" as const, label: "cache allowed", color: c.cached },
  ];
  const totalField = [
    { key: "total_cost" as const, label: "total spend", color: c.total },
  ];

  const lastSkip = lastPopulatedIndex(rows, "skip_cache_avg_cost");
  const lastCached = lastPopulatedIndex(rows, "cached_avg_cost");

  const breachDays = new Set(rows.filter((r) => r.breach_count > 0).map((r) => r.day));
  const [hoveredPanel, setHoveredPanel] = useState<"avg" | "total" | null>(null);

  // Both panels reserve identical left/right margins and y-axis widths so the
  // two plot areas line up column-for-column under the shared day axis.
  const yAxisWidth = 58;
  const chartMargin = { top: 8, right: 56, bottom: 0, left: 4 };

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Daily cost trend ({days}d)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Average run cost per audit by cache behaviour, and total spend per
            day. Run cost is infra + vendor, UTC days.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onDaysChange(w)}
              aria-pressed={w === days}
              className={`rounded px-2 py-1 text-xs border transition-colors ${
                w === days
                  ? "border-zinc-400 dark:border-zinc-500 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium"
                  : "border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200"
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-500">loading…</div>
      ) : !hasData ? (
        <div className="py-16 text-center text-sm text-zinc-500">
          No audits in the last {days} days.
        </div>
      ) : (
        <div className="space-y-1">
          {/* Panel 1 — average cost per audit, split by cache behaviour. */}
          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h3 className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Avg cost per audit
              </h3>
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-500">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-0.5 w-3 rounded-full"
                    style={{ backgroundColor: c.skipCache }}
                  />
                  skip-cache
                  {totals?.skip_cache_avg_cost != null && (
                    <span className="text-zinc-700 dark:text-zinc-300 tabular-nums">
                      {formatUsd(totals.skip_cache_avg_cost)} avg
                    </span>
                  )}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-0.5 w-3 rounded-full"
                    style={{ backgroundColor: c.cached }}
                  />
                  cache allowed
                  {totals?.cached_avg_cost != null && (
                    <span className="text-zinc-700 dark:text-zinc-300 tabular-nums">
                      {formatUsd(totals.cached_avg_cost)} avg
                    </span>
                  )}
                </span>
              </div>
            </div>
            <div
              className="h-[200px] w-full"
              onMouseEnter={() => setHoveredPanel("avg")}
              onMouseLeave={() => setHoveredPanel(null)}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={chartMargin} syncId="audit-spend-daily">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
                  <XAxis dataKey="day" hide />
                  <YAxis
                    width={yAxisWidth}
                    tick={axisTick}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={compactUsd}
                  />
                  <Tooltip
                    content={
                      <TrendTooltip
                        rows={rows}
                        fields={[...avgFields, ...totalField]}
                        suppressed={hoveredPanel === "total"}
                      />
                    }
                    cursor={{ stroke: gridStroke, strokeWidth: 1 }}
                  />
                  {/* connectNulls stays off: a day with no skip-cache audit is a
                      gap in the evidence, not a straight line through it. */}
                  <Line
                    type="monotone"
                    dataKey="skip_cache_avg_cost"
                    name="skip-cache"
                    stroke={c.skipCache}
                    strokeWidth={2}
                    connectNulls={false}
                    dot={{ r: 2.5, strokeWidth: 0, fill: c.skipCache }}
                    activeDot={{ r: 4.5, strokeWidth: 2, stroke: surface, fill: c.skipCache }}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="skip_cache_avg_cost"
                      content={lastPointLabel(c.skipCache, lastSkip)}
                    />
                  </Line>
                  <Line
                    type="monotone"
                    dataKey="cached_avg_cost"
                    name="cache allowed"
                    stroke={c.cached}
                    strokeWidth={2}
                    connectNulls={false}
                    dot={{ r: 2.5, strokeWidth: 0, fill: c.cached }}
                    activeDot={{ r: 4.5, strokeWidth: 2, stroke: surface, fill: c.cached }}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="cached_avg_cost"
                      content={lastPointLabel(c.cached, lastCached)}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Panel 2 — total spend per day. Own scale, own panel: dollars-per-day
              and cents-per-audit share a time axis, never a value axis. */}
          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h3 className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Total spend per day
              </h3>
              {totals && (
                <span className="text-[11px] text-zinc-500">
                  {formatUsd2(totals.total_cost)} over {days}d •{" "}
                  {formatInt(totals.audit_count)} audits
                </span>
              )}
            </div>
            <div
              className="h-[180px] w-full"
              onMouseEnter={() => setHoveredPanel("total")}
              onMouseLeave={() => setHoveredPanel(null)}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={chartMargin} syncId="audit-spend-daily">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
                  <XAxis
                    dataKey="day"
                    tick={axisTick}
                    axisLine={{ stroke: gridStroke, strokeWidth: 1 }}
                    tickLine={false}
                    dy={6}
                    minTickGap={16}
                    tickFormatter={formatDayShort}
                  />
                  <YAxis
                    width={yAxisWidth}
                    tick={axisTick}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={compactUsd}
                  />
                  <Tooltip
                    content={
                      <TrendTooltip
                        rows={rows}
                        fields={[...totalField, ...avgFields]}
                        suppressed={hoveredPanel === "avg"}
                      />
                    }
                    cursor={{ fill: "rgba(148,163,184,0.12)" }}
                  />
                  <Bar
                    dataKey="total_cost"
                    name="total spend"
                    fill={c.total}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={26}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {breachDays.size > 0 && (
              <BreachMarkerBand
                rows={rows}
                insetLeft={yAxisWidth + chartMargin.left}
                insetRight={chartMargin.right}
              />
            )}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {hasData && totals && (
          <span className="inline-flex items-center gap-3 text-[11px] text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: c.total }}
              />
              daily spend
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                aria-hidden
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: "4px solid transparent",
                  borderRight: "4px solid transparent",
                  borderBottom: `6px solid ${BREACH_COLOR}`,
                }}
              />
              <AlertTriangle className="h-3 w-3" style={{ color: BREACH_COLOR }} aria-hidden />
              {totals.breach_days > 0
                ? `${formatInt(totals.breach_days)} ${
                    totals.breach_days === 1 ? "day" : "days"
                  } with over-budget audits (${formatInt(totals.breach_count)} total)`
                : "no over-budget audits in this window"}
            </span>
          </span>
        )}
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
